import React, { useEffect, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { AuthService } from '../../../../authentication/service';
import { Campaign, IUserNotification } from '../../../../model/campaign/Campaign';
import NotificationItem from '../../../../shared/components/alert/NotificationItem';
import { NotificationItemSkeleton } from '../../../../shared/components/alert/NotificationItemSkeleton';
import { CustomAutocomplete } from '../../../../shared/components/Autocomplete';
import usePagination from '../../../../shared/hooks/usePagination';
import { ErrorToast } from '../../../../utils/toasters';

const SKELETON_COUNT = 8;
const LOAD_MORE_SKELETON_COUNT = 3;

const UserNotifications = () => {
    const { t } = useTranslation();
    const [campaignsDropdown, setCampaignsDropdown] = useState<Campaign[]>([]);
    const { filterParams, setFilterParams, setCount, count } = usePagination({
        perPage: 30,
        otherKeys: {
            isDetailed: true,
        },
    });
    const [notificationList, setNotificationList] = useState<IUserNotification[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const lastQueryKeyRef = useRef<string>();

    useEffect(() => {
        // Skelet samo za novi upit (prvo učitavanje, promjena kampanje);
        // sljedeća stranica se dodaje na postojeću listu, uz loader na dnu
        const queryKey = JSON.stringify({ ...filterParams, page: undefined });
        const isNewQuery = lastQueryKeyRef.current !== queryKey;
        if (isNewQuery) {
            lastQueryKeyRef.current = queryKey;
            setIsLoading(true);
        }
        const shouldReplace = isNewQuery || filterParams.page === 1;

        let isCurrent = true;
        AuthService.getNotifications(filterParams)
            .then(response => {
                if (!isCurrent) return;
                setCount(response?.count);
                setNotificationList(prev => (shouldReplace ? response.data : [...prev, ...response?.data]));
            })
            .catch(err => ErrorToast(err))
            .finally(() => {
                if (isCurrent) setIsLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [filterParams]);

    useEffect(() => {
        AuthService.getNotificationCampaigns({ page: 1, perPage: 0 })
            .then(response => {
                setCampaignsDropdown(response?.data);
            })
            .catch(err => ErrorToast(err));
    }, []);

    return (
        <Card>
            <div
                className="d-md-flex align-items-center justify-content-md-between px-4 py-3 bg-white position-sticky border-top border-bottom"
                style={{ top: 70, zIndex: 1000 }}
            >
                <h5 className="mb-md-0">{t('brand.account.myNotifications')}</h5>
                <div className="w-md-30 w-100">
                    <CustomAutocomplete
                        dataArray={campaignsDropdown}
                        isWithImage={false}
                        handleChangeParams={(_, paramValue) => {
                            setFilterParams((prev: any) => ({ ...prev, campaignId: paramValue, page: 1 }));
                        }}
                        inputLabel={'title'}
                        inputLabelTranslation={t('influencer.planner.filterByCampaign')}
                        queryFilterKey={'campaignId'}
                        isDisabled={isLoading}
                    />
                </div>
            </div>
            <Card.Body>
                {isLoading ? (
                    <div className="notification-dropdown">
                        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                            <NotificationItemSkeleton key={i} />
                        ))}
                    </div>
                ) : (
                    <InfiniteScroll
                        dataLength={notificationList?.length}
                        next={() => {
                            setFilterParams((prev: any) => ({ ...prev, page: prev?.page + 1 }));
                        }}
                        hasMore={notificationList?.length < count}
                        loader={
                            <div className="notification-dropdown">
                                {Array.from({ length: LOAD_MORE_SKELETON_COUNT }).map((_, i) => (
                                    <NotificationItemSkeleton key={i} />
                                ))}
                            </div>
                        }
                        endMessage={
                            <p
                                className={`text-center ${
                                    notificationList?.length !== count || filterParams?.perPage <= 50 ? 'd-none' : ''
                                }`}
                            >
                                <b>You have seen it all</b>
                            </p>
                        }
                    >
                        <div className="notification-dropdown">
                            {!!notificationList?.length ? (
                                notificationList.map(notification => (
                                    <NotificationItem
                                        key={notification?.id}
                                        setNotificationList={setNotificationList}
                                        notification={notification}
                                        setFilterParams={setFilterParams}
                                    />
                                ))
                            ) : (
                                <div className="py-4">
                                    <div className="text-center">
                                        <img src="/icons/empty-notification.svg" alt="Empty notification icon" />
                                        <h6 className="text-muted mb-0 mt-2">No new notifications</h6>
                                    </div>
                                </div>
                            )}
                        </div>
                    </InfiniteScroll>
                )}
            </Card.Body>
        </Card>
    );
};

export default UserNotifications;
